interface SpeakHandlers {
  onStart?: () => void;
  onEnd?: () => void;
}

export function canSpeak(): boolean {
  return typeof window !== "undefined" && "speechSynthesis" in window;
}

export function primeVoices(): () => void {
  if (!canSpeak()) return () => undefined;
  const synth = window.speechSynthesis;
  const load = () => synth.getVoices();
  load();
  synth.addEventListener("voiceschanged", load);
  return () => synth.removeEventListener("voiceschanged", load);
}

export function speak(
  text: string,
  lang: string,
  handlers: SpeakHandlers = {},
  rate = 0.9
): boolean {
  if (!canSpeak()) {
    handlers.onEnd?.();
    return false;
  }
  const synth = window.speechSynthesis;
  synth.cancel();

  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = lang;
  utterance.rate = rate;

  const voices = synth.getVoices();
  const target = lang.toLowerCase();
  const base = target.split("-")[0];
  const voice =
    voices.find((v) => v.lang.toLowerCase() === target) ??
    voices.find((v) => v.lang.toLowerCase().replace("_", "-").startsWith(base));
  if (voice) utterance.voice = voice;

  utterance.onstart = () => handlers.onStart?.();
  utterance.onend = () => handlers.onEnd?.();
  utterance.onerror = () => handlers.onEnd?.();

  synth.speak(utterance);
  return true;
}

/* ---------- Speech recognition (Chrome / Edge) ---------- */

interface RecognitionAlternative {
  transcript: string;
}
interface RecognitionResultEvent {
  results: ArrayLike<ArrayLike<RecognitionAlternative>>;
}
export interface Recognizer {
  lang: string;
  interimResults: boolean;
  maxAlternatives: number;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((e: RecognitionResultEvent) => void) | null;
  onerror: ((e: { error: string }) => void) | null;
  onend: (() => void) | null;
}
type RecognizerCtor = new () => Recognizer;

function getCtor(): RecognizerCtor | null {
  if (typeof window === "undefined") return null;
  const w = window as unknown as {
    SpeechRecognition?: RecognizerCtor;
    webkitSpeechRecognition?: RecognizerCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export function canRecognize(): boolean {
  return getCtor() !== null;
}

export function createRecognizer(lang: string): Recognizer | null {
  const Ctor = getCtor();
  if (!Ctor) return null;
  const rec = new Ctor();
  rec.lang = lang;
  rec.interimResults = false;
  rec.maxAlternatives = 3;
  return rec;
}
